import axios from "axios";
import { config } from "../config/config";
import { authConstants } from "../config/auth-costants";

const getAccessToken = (): string | null => {
  return localStorage.getItem(authConstants.storageAccessTokenKey);
};

const getRefreshToken = (): string | null => {
  return localStorage.getItem(authConstants.storageRefreshTokenKey);
};

const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(authConstants.storageAccessTokenKey, accessToken);
  localStorage.setItem(authConstants.storageRefreshTokenKey, refreshToken);
};

const clearTokens = (): void => {
  localStorage.removeItem(authConstants.storageAccessTokenKey);
  localStorage.removeItem(authConstants.storageRefreshTokenKey);

  window.dispatchEvent(
    new CustomEvent(authConstants.refreshTokenExpiredEventKey),
  );
};

export const client = axios.create({
  baseURL: config.BACKEND_URL,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

client.interceptors.request.use(
  async (axiosConfig) => {
    const token = getAccessToken();
    if (token) {
      axiosConfig.headers.Authorization = `Bearer ${token}`;
    }
    return axiosConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);

client.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearTokens();
        processQueue(error, null);
        isRefreshing = false;

        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${config.BACKEND_URL}/auth/refresh-token`,
          {
            refreshToken: refreshToken,
          },
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          response.data;

        setTokens(newAccessToken, newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        return client(originalRequest);
      } catch (refreshError) {
        clearTokens();
        processQueue(refreshError, null);

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
